import { LitElement, html, css } from 'lit';
import { audioStyles } from './audio-styles.js'
import { customElement } from 'lit/decorators/custom-element.js';
import { query } from 'lit/decorators/query.js';
import { property, state } from 'lit/decorators.js';
// Register with a decorator
@customElement('audio-player')

export class AudioPlayer extends LitElement {  
  constructor() {
    super();
    this.nonAudioAttributes = new Set(['title', 'bar-width', 'bar-gap', 'buffer-percentage']);
  }  

  @state() playing = false;
  @state() volume = 0.4;
  @state() prevVolume = 0.4;
  @state() initialized = false;
  @state() barWidth = 3;
  @state() barGap = 1;
  @state() bufferPercentage = 75;  

  @property() src;
  @property() muted;
  @property() crossorigin;
  @property() loop;
  @property() preload;
  @property() autoplay;
  /* non-audio attributes */
  // name of audio
  @property() title;
  // size of frequency bar
  @property() 'bar-width';
  // size of gap between bars
  @property() 'bar-gap';
  // percentage of frequency buffer data to represent on canvas
  @property() 'buffer-percentage';

  // create a getter that returns the audio node from render root
  @query('audio') _audio;
  
  static styles = [
    audioStyles,
    css`
      :host {
      width: 100%;
      max-width: 400px;
      font-family: sans-serif;
      display: inline-block;      
      }
      
      * {
        box-sizing: border-box;
      }
    `
  ]

  updateAudioAttributes(name, value) {
    // if this.audio is null or a non audio attribute is changed,
    // then there is nothing to update
    if (!this.audio || this.nonAudioAttributes.has(name)) return;
    
    // Set if attribute explicitly on the audio-player tag,
    // otherwise remove it
    if (this.attributes.getNamedItem(name)) {
      this.audio.setAttribute(name, value ?? '')
    } else {
      this.audio.removeAttribute(name);
    }
  }
  
  initializeAudio() {
    if (this.initialized) return;
    
    this.initialized = true;
    
    this.audioCtx = new AudioContext();
    this.gainNode = this.audioCtx.createGain();
    this.analyserNode = this.audioCtx.createAnalyser();
    this.track = this.audioCtx.createMediaElementSource(this.audio);
    
    this.analyserNode.fftSize = 2048;
    this.bufferLength = this.analyserNode.frequencyBinCount;
    this.dataArray = new Uint8Array(this.bufferLength);
    this.analyserNode.getByteFrequencyData(this.dataArray);
    
    this.track
        .connect(this.gainNode)
        .connect(this.analyserNode)
        .connect(this.audioCtx.destination);
    
    this.changeVolume();
  }

  updateFrequency() {
    if (!this.playing) return;
    
    this.analyserNode.getByteFrequencyData(this.dataArray);
    
    this.canvasCtx.clearRect(0, 0, this.canvas.width, this.canvas.height)
    this.canvasCtx.fillStyle = "rgba(0, 0, 0, 0)";
    this.canvasCtx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    
    const barCount = (this.canvas.width / (this.barWidth + this.barGap)) - this.barGap;
    const bufferSize = (this.bufferLength * this.bufferPercentage) / 100;
    let x = 0;
    
    // this is a loss representation of the frequency
    // some data are loss to fit the size of the canvas
    for (let i = 0; i < barCount; i++) {
      // get percentage of i value
      const iPerc = Math.round((i * 100) / barCount);
      // what the i percentage maps to in the frequency data
      const pos = Math.round((bufferSize * iPerc) / 100);
      const frequency = this.dataArray[pos];
      // frequency value in percentage
      const frequencyPerc = (frequency * 100) / 255;
      // frequency percentage value in pixel in relation to the canvas height
      const barHeight = (frequencyPerc * this.canvas.height) / 100;
      // flip the height so the bar is drawn from the bottom
      const y = this.canvas.height - barHeight;
      
      this.canvasCtx.fillStyle = `rgba(${frequency}, 255, 100)`;
      this.canvasCtx.fillRect(x, y, this.barWidth, barHeight);
      
      x += (this.barWidth + this.barGap);
    }
    
    requestAnimationFrame(this.updateFrequency.bind(this))
  }

  attachEvents() {
    this.volumeBar.parentNode.addEventListener('click', e => {
      if (e.target === this.volumeBar.parentNode) {
        this.toggleMute();
      }
    }, false);
    
    this.playPauseBtn.addEventListener('click', this.togglePlay.bind(this), false);
    
    this.volumeBar.addEventListener('input', this.changeVolume.bind(this), false);
    
    this.progressBar.addEventListener('input', (e) => this.seekTo(this.progressBar.value), false);
    
    this.audio.addEventListener('loadedmetadata', () => {
      this.progressBar.max = this.audio.duration;
      this.durationEl.textContent = this.getTimeString(this.audio.duration);
      this.updateAudioTime();
    })
    
    this.audio.addEventListener('error', (e) => {
      this.titleElement.textContent = this.audio.error.message;
      this.playPauseBtn.disabled = true;
    })
    
    this.audio.addEventListener('timeupdate', () => {
      this.updateAudioTime(this.audio.currentTime);
    })
    
    this.audio.addEventListener('ended', () => {
      this.playing = false;
      this.playPauseBtn.textContent = 'play';
      this.playPauseBtn.classList.remove('playing');
    }, false);
    
    this.audio.addEventListener('pause', () => {
      this.playing = false;
      this.playPauseBtn.textContent = 'play';
      this.playPauseBtn.classList.remove('playing');
    }, false);
    
    this.audio.addEventListener('play', () => {
      this.playing = true;
      this.playPauseBtn.textContent = 'pause';
      this.playPauseBtn.classList.add('playing');
      this.updateFrequency();
    }, false);
  }

  async togglePlay() {
    if (this.audioCtx.state === 'suspended') {
      await this.audioCtx.resume();
    }
    
    if (this.playing) {
      return this.audio.pause();
    }

    // play() returns a promise that is resolved when playback has startd
    // is a method of the HTMLMediaElement and attempts to begin playback
    return this.audio.play();
  }
  
  getTimeString(time) {
    const secs = `${parseInt(`${time % 60}`, 10)}`.padStart(2, '0');
    const min = parseInt(`${(time / 60) % 60}`, 10);
    
    return `${min}:${secs}`;
  }
  
  changeVolume() {
    this.volume = Number(this.volumeBar.value);
    
    if (Number(this.volume) > 1) {
      this.volumeBar.parentNode.className = 'volume-bar over';
    } else if (Number(this.volume) > 0) {
      this.volumeBar.parentNode.className = 'volume-bar half';
    } else {
      this.volumeBar.parentNode.className = 'volume-bar';
    }
    
    if (this.gainNode) {
      this.gainNode.gain.value = this.volume;
    }
  }
  
  toggleMute(muted = null) {
    this.volumeBar.value = muted || this.volume === 0 ? this.prevVolume : 0;
    this.changeVolume();
  }
  
  seekTo(value) {
    this.audio.currentTime = value;
  }
  
  updateAudioTime() {
    this.progressBar.value = this.audio.currentTime;
    this.currentTimeEl.textContent = this.getTimeString(this.audio.currentTime);
  }  
  
  // called before update() to compute values needed during the update
  willUpdate(changedProperties) {
    if(changedProperties.has('src')) {
      this.initialized = false;
    }

    // all audio attributes need to be reset if rendering or re-rendering 
    for (let i = 0; i < changedProperties; i++) {
      const attr = changedProperties[i];
      this.updateAudioAttributes(attr.name, attr.value);
    }        
  }

  // called after the component's DOM has been updated the first time,
  // immediately before updated()
  firstUpdated() {
    this.audio = this.renderRoot.querySelector('audio');
    this._audio.src = this.attributes.src.value;
    this.volumeBar = this.renderRoot.querySelector('.volume-field');    
    this.initializeAudio();

    this.playPauseBtn = this.renderRoot.querySelector('.play-btn');
    this.titleElement = this.renderRoot.querySelector('.audio-name');
    this.progressIndicator = this.renderRoot.querySelector('.progress-indicator');
    this.currentTimeEl = this.progressIndicator.children[0];
    this.progressBar = this.progressIndicator.children[1];
    this.durationEl = this.progressIndicator.children[2];
    this.canvas = this.renderRoot.querySelector('canvas');

    this.canvasCtx = this.canvas.getContext("2d");
    // support retina display on canvas for a more crispy/HD look
    const scale = window.devicePixelRatio;
    this.canvas.width = Math.floor(this.canvas.width* scale);
    this.canvas.height = Math.floor(this.canvas.height * scale);
    this.titleElement.textContent = this.attributes.getNamedItem('src')
                                  ? this.attributes.getNamedItem('title').value ?? 'untitled'
                                  : 'No Audio Source Provided';
    this.volumeBar.value = this.volume;
    
    this.attachEvents();
  }
  
  render() {    
    return html`
      <figure class="audio-player">
        <figcaption class="audio-name"></figcaption>
        <audio style="display: none"></audio>
        <button class="play-btn" type="button">play</button>
        <div class="progress-indicator">
          <span class="current-time">0:0</span>
          <input class="progress-bar" type="range" max="100" value="0" />
          <span class="duration">0:00</span>
          <canvas class="visualizer" style="width: 100%; height: 20px"></canvas>
        </div>
        <div class="volume-bar">
          <input class="volume-field"
                 type="range"
                 min="0"
                 max="2"
                 step="0.01"
                 value="${this.volume}" />                  
        </div>
      </figure>
    `;
  }
}
